import React, { useState } from 'react';
import { Header, HeaderLogo, Typography } from '@revlab/highlander-ui';
import images from '../../assets/Images';
import { Language, Person, Logout } from '@mui/icons-material';
import { COLORS } from '../../colors';
import { Box, Button, useMediaQuery, useTheme, IconButton, Tooltip, Menu, MenuItem } from '@mui/material';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { logoutRequest } from '../../auth/msalConfig';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'en', name: 'English', displayCode: 'EN' },
  { code: 'es', name: 'Spanish', displayCode: 'ES' },
  { code: 'fr', name: 'French', displayCode: 'FR' },
  { code: 'de', name: 'German', displayCode: 'DE' },
  { code: 'it', name: 'Italian', displayCode: 'IT' },
  { code: 'nl', name: 'Dutch', displayCode: 'NL' }
];

export const HeaderSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const name = accounts[0]?.name || '';
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);

  const handleLogout = async () => {
    try {
      await instance.logoutRedirect(logoutRequest);
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageAnchorEl(null);
  };

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem('i18nextLng', languageCode);
    handleLanguageMenuClose();
  };

  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === i18n.language) || languages[0];
  };

  return (
    <Header isSticky>
      <Box
        sx={{
          width: '100%',
          px: { xs: 1, sm: 4 },
          py: 0,
          minHeight: { xs: 56, sm: 72 },
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        {/* Brand Logos - always centered horizontally, shrink/stack on mobile */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            gap: { xs: 1.5, sm: 3 },
            minWidth: 0,
            flexDirection: 'row',
            position: 'absolute',
            left: isMobile ? '25%' : '50%',
            transform: 'translateX(-50%)',
            top: 0,
            bottom: 0,
            height: '100%',
            zIndex: 2,
          }}
        >
          <HeaderLogo href="/" image={{ alt: "hertz logo", src: images.hertzLogo }} />
          <HeaderLogo href="/" image={{ alt: "dollar logo", src: images.DollarLogo }} />
          <HeaderLogo href="/" image={{ alt: "thrifty logo", src: images.ThriftyLogo }} />
        </Box>

        {/* Language + User - always right-aligned, never overlaps logos */}
        <Box
          sx={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            flexDirection: isMobile ? 'column' : 'row',
            zIndex: 3,
            minWidth: 0,
            pr: { xs: 1, sm: 6, lg: 12 }
          }}
        >
          {isAuthenticated && (
            <>
              <Button
                startIcon={<Language sx={{ color: COLORS.darkGray }} />}
                sx={{ textTransform: 'none', color: COLORS.darkGray, fontSize: 16, fontWeight: 600, minWidth: 0, px: 1 }}
                onClick={handleLanguageMenuOpen}
              >
                {getCurrentLanguage().displayCode}
              </Button>
              <Menu
                anchorEl={languageAnchorEl}
                open={Boolean(languageAnchorEl)}
                onClose={handleLanguageMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {languages.map((lang) => (
                  <MenuItem
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    selected={i18n.language === lang.code}
                    sx={{
                      minWidth: 180,
                      display: 'flex',
                      justifyContent: 'space-between',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      },
                    }}
                  >
                    <span>{lang.name}</span>
                    <span style={{ marginLeft: 16, color: COLORS.darkGray, fontWeight: 600 }}>{lang.displayCode}</span>
                  </MenuItem>
                ))}
              </Menu>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person fontSize="small" sx={{ color: COLORS.darkGray }} />
                <Typography variant="body2" sx={{ color: COLORS.darkGray, fontSize: 16, fontWeight: 600 }}>
                  {name}
                </Typography>
              </Box>
              <Tooltip title={t('app.logout', 'Logout')}>
                <IconButton
                  onClick={handleLogout}
                  sx={{
                    color: COLORS.darkGray,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Logout />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    </Header>
  );
};
